import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Paper, TextField, MenuItem, Select, FormControl, InputLabel,
  IconButton, Typography, Grid, SelectChangeEvent, Chip, CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon, Refresh as RefreshIcon,
  ArrowDownward as ArrowDownIcon, ArrowUpward as ArrowUpIcon,
} from '@mui/icons-material';
import { adminStyles } from '../../../theme/adminStyles';
import axios from 'axios';
import i18next from 'i18next';

interface LogFile {
  name: string;
  size: number;
  lastModified: number;
}

const LOG_LEVELS = ['', 'TRACE', 'DEBUG', 'INFO', 'WARN', 'ERROR'];
const LINES_PER_PAGE = 200;

const levelColor = (line: string): string | undefined => {
  if (line.includes('- ERROR ')) return '#ef5350';
  if (line.includes('- WARN ')) return '#ff9800';
  if (line.includes('- DEBUG ')) return '#90caf9';
  if (line.includes('- TRACE ')) return '#78909c';
  return undefined;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const ServerLogTab = () => {
  const [files, setFiles] = useState<LogFile[]>([]);
  const [selectedFile, setSelectedFile] = useState('tamai_ec.log');
  const [lines, setLines] = useState<string[]>([]);
  const [fileSize, setFileSize] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextByteOffset, setNextByteOffset] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [level, setLevel] = useState('');
  const [appliedLevel, setAppliedLevel] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef(false);

  const fetchFiles = useCallback(async () => {
    try {
      const res = await axios.get('/api/v1/admin/service-status/server-logs/files');
      setFiles(res.data || []);
    } catch (err) {
      console.error('Failed to fetch log files', err);
    }
  }, []);

  // 初回ロード（最新ログを取得）
  const fetchInitial = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        fileName: selectedFile,
        lines: LINES_PER_PAGE,
        byteOffset: 0,
      };
      if (appliedKeyword) params.keyword = appliedKeyword;
      if (appliedLevel) params.level = appliedLevel;

      const res = await axios.get('/api/v1/admin/service-status/server-logs', { params });
      setLines(res.data.lines || []);
      setFileSize(res.data.fileSize || 0);
      setHasMore(res.data.hasMore || false);
      setNextByteOffset(res.data.nextByteOffset || 0);

      // 最新行を表示するため下へスクロール
      setTimeout(() => {
        if (logContainerRef.current) {
          logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }
      }, 50);
    } catch (err) {
      console.error('Failed to fetch server logs', err);
    } finally {
      setLoading(false);
    }
  }, [selectedFile, appliedKeyword, appliedLevel]);

  // 古いログを追加ロード（infinite scroll）
  const fetchMore = useCallback(async () => {
    if (!hasMore || isLoadingMoreRef.current) return;
    isLoadingMoreRef.current = true;
    setLoadingMore(true);

    try {
      const params: Record<string, string | number> = {
        fileName: selectedFile,
        lines: LINES_PER_PAGE,
        byteOffset: nextByteOffset,
      };
      if (appliedKeyword) params.keyword = appliedKeyword;
      if (appliedLevel) params.level = appliedLevel;

      const container = logContainerRef.current;
      const prevScrollHeight = container?.scrollHeight || 0;

      const res = await axios.get('/api/v1/admin/service-status/server-logs', { params });
      const olderLines: string[] = res.data.lines || [];

      setLines(prev => [...olderLines, ...prev]);
      setHasMore(res.data.hasMore || false);
      setNextByteOffset(res.data.nextByteOffset || 0);

      // スクロール位置を維持（新しい行が上に追加された分だけ調整）
      requestAnimationFrame(() => {
        if (container) {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = newScrollHeight - prevScrollHeight;
        }
      });
    } catch (err) {
      console.error('Failed to fetch more logs', err);
    } finally {
      setLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
  }, [hasMore, nextByteOffset, selectedFile, appliedKeyword, appliedLevel]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);
  useEffect(() => { fetchInitial(); }, [fetchInitial]);

  // スクロール監視（上端に近づいたら追加ロード）
  useEffect(() => {
    const container = logContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (container.scrollTop < 100 && hasMore && !isLoadingMoreRef.current) {
        fetchMore();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMore, fetchMore]);

  const handleSearch = () => {
    setAppliedKeyword(keyword);
    setAppliedLevel(level);
  };

  const handleReset = () => {
    setKeyword('');
    setLevel('');
    setAppliedKeyword('');
    setAppliedLevel('');
  };

  const handleFileChange = (e: SelectChangeEvent) => {
    setSelectedFile(e.target.value);
    setLines([]);
    setHasMore(false);
    setNextByteOffset(0);
  };

  const handleRefreshLatest = () => {
    fetchInitial();
  };

  const scrollToTop = () => {
    if (logContainerRef.current) logContainerRef.current.scrollTop = 0;
  };

  const scrollToBottom = () => {
    if (logContainerRef.current) logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
  };

  const selectedFileInfo = files.find(f => f.name === selectedFile);

  return (
    <>
      <Paper sx={adminStyles.filterPaper}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{i18next.t('service_status.log_file')}</InputLabel>
              <Select
                value={selectedFile}
                label={i18next.t('service_status.log_file')}
                onChange={handleFileChange}
              >
                {files.map(f => (
                  <MenuItem key={f.name} value={f.name}>
                    {f.name} ({formatFileSize(f.size)})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth size="small"
              label={i18next.t('service_status.keyword')}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{i18next.t('service_status.log_level')}</InputLabel>
              <Select
                value={level}
                label={i18next.t('service_status.log_level')}
                onChange={(e: SelectChangeEvent) => setLevel(e.target.value)}
              >
                {LOG_LEVELS.map(l => (
                  <MenuItem key={l} value={l}>
                    {l === '' ? i18next.t('service_status.all') : l}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton color="primary" onClick={handleSearch}><SearchIcon /></IconButton>
              <IconButton onClick={handleReset}><RefreshIcon /></IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ ...adminStyles.tablePaper, p: 0, display: 'flex', flexDirection: 'column' }}>
        {/* ステータスバー */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          px: 2, py: 0.5, borderBottom: '1px solid rgba(0,0,0,0.1)', backgroundColor: 'rgba(0,0,0,0.02)',
        }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              {selectedFile}
            </Typography>
            {selectedFileInfo && (
              <Chip label={formatFileSize(selectedFileInfo.size)} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
            )}
            <Typography variant="caption" color="text.secondary">
              {fileSize > 0 && ` | ${formatFileSize(fileSize)}`}
              {` | ${i18next.t('service_status.showing')}: ${lines.length.toLocaleString()} ${i18next.t('service_status.lines')}`}
              {hasMore && ` | ${i18next.t('service_status.scroll_for_more')}`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" onClick={handleRefreshLatest} title="Refresh">
              <RefreshIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
            <IconButton size="small" onClick={scrollToTop} title="Top"><ArrowUpIcon sx={{ fontSize: '1rem' }} /></IconButton>
            <IconButton size="small" onClick={scrollToBottom} title="Bottom"><ArrowDownIcon sx={{ fontSize: '1rem' }} /></IconButton>
          </Box>
        </Box>

        {/* ログ表示 */}
        <Box
          ref={logContainerRef}
          sx={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: '#1e1e1e',
            color: '#d4d4d4',
            fontFamily: '"Cascadia Code", "Fira Code", "Consolas", "Monaco", monospace',
            fontSize: '0.75rem',
            lineHeight: 1.5,
            p: 1.5,
            minHeight: 400,
            maxHeight: 'calc(100vh - 380px)',
            whiteSpace: 'pre',
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: '#2d2d2d' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#555', borderRadius: 4 },
          }}
        >
          {/* 追加ロード中インジケータ */}
          {loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={20} sx={{ color: '#666' }} />
            </Box>
          )}
          {hasMore && !loadingMore && (
            <Box sx={{ textAlign: 'center', py: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#555', fontSize: '0.7rem' }}>
                ↑ {i18next.t('service_status.scroll_for_more')}
              </Typography>
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress size={32} sx={{ color: '#666' }} />
            </Box>
          ) : lines.length === 0 ? (
            <Typography sx={{ color: '#666', fontFamily: 'inherit', fontSize: 'inherit', textAlign: 'center', mt: 4 }}>
              {i18next.t('service_status.no_data')}
            </Typography>
          ) : (
            lines.map((line, idx) => (
              <Box
                key={idx}
                component="div"
                sx={{
                  color: levelColor(line) || '#d4d4d4',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                  px: 0.5,
                }}
              >
                <Box component="span" sx={{ color: '#666', userSelect: 'none', mr: 1, display: 'inline-block', minWidth: 48, textAlign: 'right' }}>
                  {idx + 1}
                </Box>
                {line}
              </Box>
            ))
          )}
        </Box>
      </Paper>
    </>
  );
};

export default ServerLogTab;
